import isEmpty from 'lodash/isEmpty';
import mapValues from 'lodash/mapValues';
import includes from 'lodash/includes';
import querystring from 'querystring';
import { parseUrlChannel } from '../../../shared/model/constants/channels';
import { parseUrlTenureType } from '../../../shared/model/constants/tenureType';
import searchConditionExtractor from './searchConditionExtractor';
import locationUrl from './url/locationUrl';
import keywordsUrl from './url/keywordsUrl';
import { getSortOptionByValue } from './query/sortOptions';
import { validateParkingSpaces } from './MinParkingSpaces';
import { parseUrlPropertyType } from '../../../shared/model/constants/propertyTypes';

const urlRegexp = new RegExp([
  '^/(for-sale|for-lease|sold|leased|sold-leased|invest)/', // channel
  '([^?]+)?', // path with location, price and prop type
].join(''));

const propertiesToSanitise = [
  'minPrice',
  'maxPrice',
  'minSiteArea',
  'maxSiteArea',
  'minFloorArea',
  'maxFloorArea',
];

const sanitiseNumber = number => parseFloat(number) || undefined;

const parseEnergyEfficiency = energyEfficiency => (
  includes(['1', '2', '3', '4', '5', '6'], energyEfficiency)
    ? Number(energyEfficiency)
    : undefined
);

const switchMinMax = (min, max) =>
  (min > max ? [max, min] : [min, max]);

const switchMinMaxFor = (minKeyName, maxKeyName) => (refinements) => {
  const [minVal, maxVal] = switchMinMax(refinements[minKeyName], refinements[maxKeyName]);
  const newMinMax = {
    [minKeyName]: minVal,
    [maxKeyName]: maxVal,
  };

  return {
    ...refinements,
    ...newMinMax,
  };
};

const unifyQueryAndPathLocations = refinements => ({
  ...refinements,
  // localities comes from path, locations from query, never truthy at the same time
  localities: refinements.locations || refinements.localities,
  locations: undefined,
});

const propertyTypesFromQueryOrPath = (pathPropertyType, queryPropertyTypesString) => {
  if (!isEmpty(pathPropertyType)) {
    return pathPropertyType;
  }

  const queryPropertyTypeUrls = queryPropertyTypesString ? queryPropertyTypesString.split(',') : [];
  const queryPropertyTypeObjects = queryPropertyTypeUrls.map(propertyUrl => parseUrlPropertyType(propertyUrl)).filter(Boolean);

  return !isEmpty(queryPropertyTypeObjects) ? queryPropertyTypeObjects : undefined;
};

const parsePageNumber = (page) => {
  const pageAsInteger = parseInt(page, 10);

  return pageAsInteger > 0 ? pageAsInteger : 1;
};

const processPath = (relativeUrl) => {
  const matches = relativeUrl.match(urlRegexp);

  if (!matches) {
    return null;
  }
  const [, channelMatch, searchCondition] = matches;
  const channelObject = parseUrlChannel(channelMatch);
  const { propertyTypeObject, locationsUrl } = searchConditionExtractor(searchCondition);
  const localities = locationUrl.parse(locationsUrl);
  const propertyTypes = propertyTypeObject ? [propertyTypeObject] : undefined;

  return {
    channelObject,
    localities,
    propertyTypes,
  };
};

const buildQuery = (rawQuery = {}, pathRefinements = {}) => {
  const queryWithRefinements = {
    ...rawQuery,
    ...pathRefinements,
  };
  const sanitisedQuery = mapValues(
    queryWithRefinements, (value, key) => (
      includes(propertiesToSanitise, key)
        ? sanitiseNumber(value)
        : value
    ),
  );

  return {
    ...sanitisedQuery,
    locations: locationUrl.parse(sanitisedQuery.locations),
    keywords: keywordsUrl.parse(sanitisedQuery.keywords),
    numParkingSpaces: validateParkingSpaces(sanitisedQuery.numParkingSpaces),
    energyEfficiency: parseEnergyEfficiency(sanitisedQuery.energyEfficiency),
  };
};

export default function refinementsFromUrl(relativeUrl) {
  const queryStr = relativeUrl.split('?')[1];
  const pathRefinements = processPath(relativeUrl) || {};
  const rawQuery = queryStr ? querystring.parse(queryStr) : {};
  const query = buildQuery(rawQuery, pathRefinements);
  const finalQuery = [query]
    .map(switchMinMaxFor('minPrice', 'maxPrice'))
    .map(switchMinMaxFor('minFloorArea', 'maxFloorArea'))
    .map(switchMinMaxFor('minSiteArea', 'maxSiteArea'))
    .map(unifyQueryAndPathLocations)[0];

  return {
    ...finalQuery,
    tenureTypeObject: parseUrlTenureType(rawQuery.tenure),
    sortObject: getSortOptionByValue(rawQuery.activeSort),
    propertyTypes: propertyTypesFromQueryOrPath(pathRefinements.propertyTypes, rawQuery.propertyTypes),
    page: parsePageNumber(rawQuery.page),
  };
}
